import { useProfile } from "@/context/useProfileProps";
import { getWebsiteNameFromURL } from "@/helpers/getWebsiteNameFromURL";
import { handleFocusOnField } from "@/helpers/helper";
import { LINKEDIN_REGEX, LINK_REGEX } from "@/utils/regex";
import { Input, Label } from "@monsterindia/global-ui-components";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import AutoUpdateKnowMore from "@/components/common/autoUpdate/autoUpdateKnowMore";
import AutoUpdateToggleWarning from "@/components/common/autoUpdate/autoUpdateToggleWarning";
import ProfileModal from "@/components/common/profileModal";

import { useSocialLinks, useWorkSampleLinks } from "./query";
import {
  EditCardProps,
  MutationPyloadType,
  SocialLinkFormValues,
} from "./types";

const EditCard = ({
  isEdit,
  closeEdit,
  prefilledData,
  isCalledFromOutside = false,
  formRef,
  handleFooterBtnLoading,
  isLinkedAdded = false,
  focusOnField = "",
}: EditCardProps) => {
  const { mutate: mutateSocialLinks } = useSocialLinks();
  const { mutate: mutateWorkSample } = useWorkSampleLinks();

  const {
    isMobile,
    translation: { socialLinks, errorMessages },
    showToast,
    handleEventTracking,
  } = useProfile();

  const { id, url } = prefilledData;
  const workTitle =
    "workTitle" in prefilledData ? prefilledData.workTitle : null;

  const [isLinkedURL, setIsLinkedURL] = useState(false);
  const [isLinkedinAutoUpdateChecked, setIsLinkedinAutoUpdateChecked] =
    useState(false);
  const [linkError, setLinkError] = useState("");
  const [showLinkedinWarning, setShowLinkedinWarning] = useState(false);
  const [loading, setLoading] = useState({
    isSaveLoading: false,
    isDeleteLoading: false,
  });

  useEffect(() => {
    handleFocusOnField(focusOnField, "social-link-form");
  }, [focusOnField]);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    clearErrors,
    reset,
  } = useForm<SocialLinkFormValues>({
    defaultValues: {
      id: id || undefined,
      url: url || "",
    },
    mode: "onBlur",
  });

  const handleSaveBtnLoading = (val: boolean) => {
    setLoading((prev) => ({ ...prev, isSaveLoading: val }));
    if (handleFooterBtnLoading) {
      handleFooterBtnLoading({ isSaveLoading: val });
    }
  };

  useEffect(() => {
    if (prefilledData) {
      reset({
        id: prefilledData.id || undefined,
        url: prefilledData.url || "",
      });

      const linkedInConsent =
        "linkedInConsent" in prefilledData
          ? prefilledData.linkedInConsent
          : false;

      if (
        prefilledData.url &&
        prefilledData.url.includes("linkedin") &&
        !linkedInConsent
      ) {
        setIsLinkedURL(true);
        setIsLinkedinAutoUpdateChecked(linkedInConsent);
      } else {
        setIsLinkedURL(false);
        setIsLinkedinAutoUpdateChecked(false);
      }
    }
  }, [prefilledData, reset]);

  const onSubmit = (data: SocialLinkFormValues) => {
    if (isLinkedURL && isLinkedAdded && !url) {
      showToast({
        show: true,
        type: "error",
        message: socialLinks.linkedInAlreadyAdded,
      });
      return;
    }
    handleSaveBtnLoading(true);
    const name = getWebsiteNameFromURL(data.url);

    const isWorkSample =
      name === "Github" || name === "Behance" || name === "Kaggle";
    const payload = [
      {
        ...(prefilledData ?? {}),
        id: data.id ?? 0,
        url: data.url ?? "",
      },
    ];

    if (data.url.includes("linkedin") && !workTitle) {
      payload[0] = {
        ...payload[0],
        linkedInConsent: isLinkedinAutoUpdateChecked,
      };
    }

    if (isWorkSample) {
      payload[0] = {
        ...payload[0],
        workTitle: name,
      };
    } else {
      payload[0] = {
        ...payload[0],
        name: name,
      };
    }

    const action = id ? "edit" : "add";
    const mutate = isWorkSample ? mutateWorkSample : mutateSocialLinks;

    if (payload[0].id === 0 && action === "add") {
      // @ts-ignore
      delete payload[0].id;
    }

    mutate(
      {
        data: payload as MutationPyloadType,
        action,
      },
      {
        onSuccess: () => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            type: "success",
            message: socialLinks.saveSuccessMessage,
          });
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Saved",
            eventProps: {
              Social_Links_Saved: JSON.stringify(payload),
              Source: isApp ? "app" : "web",
            },
          });
          closeEdit("save");
        },
        onError: (err) => {
          handleSaveBtnLoading(false);
          showToast({
            show: true,
            type: "error",
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
          });
        },
      },
    );
  };

  const handleDelete = () => {
    setLoading((prev) => ({ ...prev, isDeleteLoading: true }));
    if (id) {
      let payload = { id: id };
      const mutate = workTitle ? mutateWorkSample : mutateSocialLinks;
      mutate(
        {
          data: payload,
          action: "delete",
        },
        {
          onSuccess: () => {
            setLoading((prev) => ({ ...prev, isDeleteLoading: false }));
            showToast({
              show: true,
              type: "success",
              message: socialLinks.deleteSuccessMessage,
            });
            closeEdit("delete");
          },
          onError: (err) => {
            setLoading((prev) => ({ ...prev, isDeleteLoading: false }));
            showToast({
              show: true,
              type: "error",
              message:
                err instanceof Error
                  ? err.message
                  : errorMessages.somethingWentWrong,
            });
          },
        },
      );
    }
  };

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const urlBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const truncatedValue = value.split("?")[0];
    setValue("url", truncatedValue);
    clearErrors("url");

    if (value && value.includes("linkedin")) {
      setIsLinkedURL(true);
      setIsLinkedinAutoUpdateChecked(true);
    } else {
      setIsLinkedURL(false);
      setIsLinkedinAutoUpdateChecked(false);
    }
  };

  const renderForm = (
    <form
      id="social-link-form"
      ref={formRef}
      className="flex min-h-[300px] flex-col gap-5 md:min-h-[250px]"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <div className="w-full">
        <Label
          labelText="Title"
          isRequired={true}
          className="text-sm font-normal"
        >
          <Input
            id="url"
            type="url"
            placeholder={socialLinks.enterTheURL}
            {...register("url", {
              required: {
                value: true,
                message: socialLinks.urlIsRequired,
              },
              pattern: {
                value: isLinkedURL ? LINKEDIN_REGEX : LINK_REGEX,
                message: socialLinks.urlIsNotValid,
              },
            })}
            onBlur={urlBlurHandler}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              if (value && value.includes("linkedin")) {
                setIsLinkedURL(true);
              } else {
                setIsLinkedURL(false);
              }

              if (
                value &&
                !/^https?:\/\//i.test(value) &&
                !/^www\./i.test(value)
              ) {
                setLinkError("Link should start with 'https:// or www.'");
              } else {
                setLinkError("");
              }
            }}
            errorText={linkError ? linkError : errors?.url?.message}
          />
          {/* {isLinkedURL ? (
            <div
              className={`flex flex-col gap-2 rounded-[4px] p-2 ${showLinkedinWarning ? "bg-white" : "bg-[#F1E6F98C]"}`}
            >
              <AutoUpdateKnowMore
                isSocailLink={true}
                isLinkedinAutoUpdateChecked={isLinkedinAutoUpdateChecked}
                onToggleChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setIsLinkedinAutoUpdateChecked(e.target.checked);
                  if (e.target.checked) {
                    setShowLinkedinWarning(false);
                  } else {
                    setShowLinkedinWarning(true);
                  }
                }}
              />
              {showLinkedinWarning ? <AutoUpdateToggleWarning /> : null}
            </div>
          ) : null} */}
        </Label>
      </div>
    </form>
  );

  return !isCalledFromOutside ? (
    <ProfileModal
      id="social-link-modal"
      showModal={isEdit}
      closeModal={closeEdit}
      isMobile={isMobile}
      showCloseButton={true}
      screenAlignment={isMobile ? "bottom-sheet" : "center"}
      showHandleBar={true}
      footerPrimaryBtnLabel={socialLinks.saveBtnLabel}
      footerPrimaryBtnHandler={handleFormSubmit}
      footerSecondaryBtnLabel={id ? `${socialLinks.deletBtnLabel}` : ""}
      footerSecondaryBtnHandler={handleDelete}
      footerPrimaryBtnIsLoading={loading.isSaveLoading}
      footerSecondaryBtnIsLoading={loading.isDeleteLoading}
      heading={socialLinks.heading}
    >
      {renderForm}
    </ProfileModal>
  ) : (
    renderForm
  );
};

export default EditCard;
